import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import useTheme from '../../context/ThemeContext';
import useClubVocab from '../../hooks/useClubVocab';
import StudentSelect from './StudentSelect';
import { addCustomCharge } from '../../services/feeService';

// Optional category for grouping in the ledger / Finance breakdown. Unset by default — an
// uncategorized charge still works exactly as before (falls back to its description).
const CATEGORY_OPTIONS = [
  { value: '',             label: 'No category' },
  { value: 'registration', label: 'Registration' },
  { value: 'admission',    label: 'Admission' },
  { value: 'uniform',      label: 'Uniform' },
  { value: 'grading',      label: 'Grading' },
  { value: 'equipment',    label: 'Equipment' },
  { value: 'event',        label: 'Event' },
  { value: 'other',        label: 'Other' },
];

// Searchable by name, phone, or member ID — same three fields the Students page searches,
// just filtered client-side since `students` is already the full active-student list.
const chargeStudentSearchText = (s) =>
  [s.name, s.studentProfile?.studentId, s.phone].filter(Boolean).join(' ');

// The actual "add a one-off charge" form — description + category + amount, posting through
// the existing addCustomCharge endpoint (a FeePayment, never OtherIncome). Shared by
// AddChargeModal (its own fixed-overlay shell, used by the Students drawer and Payments'
// Collect tab) and the Finance → Ledger "Record income" modal's "For a student" tab (embedded
// directly, no second header/overlay — the host modal already has one).
//
// studentId: pre-fixed student (drawer context) — hides the picker entirely.
// students: the pickable list (Finance/Collect context) — only used when studentId is absent.
export default function AddChargeForm({ studentId: fixedStudentId, students = [], studentsLoading = false, onClose, onCreated }) {
  const currentTheme = useTheme()?.currentTheme;
  const v = useClubVocab();
  const [student, setStudent]         = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount]           = useState('');
  const [category, setCategory]       = useState('');
  const [error, setError]             = useState('');

  const targetId = fixedStudentId || student;

  const mutation = useMutation({
    mutationFn: () => addCustomCharge({
      studentId: targetId, amount: Number(amount), description: description.trim(),
      ...(category ? { category } : {}),
    }),
    onSuccess: () => { toast.success('Charge added!'); onCreated?.(); onClose?.(); },
    onError:   (e) => setError(e.response?.data?.message || 'Could not add charge. Please try again.'),
  });

  const submit = (e) => {
    e.preventDefault();
    setError('');
    if (!targetId)                       { setError(`Please select a ${v.member.toLowerCase()}.`); return; }
    if (!description.trim())             { setError('Please enter a description.'); return; }
    if (!amount || Number(amount) <= 0)  { setError('Enter an amount greater than 0.'); return; }
    mutation.mutate();
  };

  return (
    <form onSubmit={submit}>
      <div className="p-5 space-y-3">
        {!fixedStudentId && (
          <div>
            <label className="block text-xs font-medium text-muted mb-1">{v.member} <span className="text-red-400">*</span></label>
            <StudentSelect
              students={students}
              value={student}
              onChange={(v) => { setStudent(v); if (error) setError(''); }}
              placeholder={`Select a ${v.member.toLowerCase()}`}
              getOptionSearchText={chargeStudentSearchText}
              loading={studentsLoading}
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-muted mb-1">Description <span className="text-red-400">*</span></label>
          <input
            autoFocus={!!fixedStudentId}
            value={description}
            onChange={(e) => { setDescription(e.target.value); if (error) setError(''); }}
            placeholder="e.g., Belt exam fee, Uniform, Registration"
            className="w-full px-3 py-2.5 rounded-xl text-sm bg-card border border-divider text-secondary placeholder:text-subtle focus:outline-none focus:border-secondary transition-colors"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-muted mb-1">Category (optional)</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl text-sm bg-card border border-divider text-secondary focus:outline-none focus:border-secondary transition-colors"
          >
            {CATEGORY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-muted mb-1">Amount (NPR) <span className="text-red-400">*</span></label>
          <input
            type="number"
            min="1"
            value={amount}
            onChange={(e) => { setAmount(e.target.value); if (error) setError(''); }}
            placeholder="Amount in NPR"
            className="w-full px-3 py-2.5 rounded-xl text-sm bg-card border border-divider text-secondary placeholder:text-subtle focus:outline-none focus:border-secondary transition-colors"
          />
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>

      <div className="flex gap-3 px-5 pb-5">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-4 py-2.5 rounded-full border border-divider text-sm font-medium text-secondary hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={mutation.isPending}
          className={`flex-1 px-4 py-2.5 rounded-full text-sm font-medium ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {mutation.isPending ? 'Adding…' : 'Create Charge'}
        </button>
      </div>
    </form>
  );
}
